import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { Observable } from 'rxjs';
import { catchError, switchMap, tap, map } from 'rxjs/operators';

import { AppConfig } from 'app.config';
import { RenderConfiguration } from 'render/render.config';
import { PageForRender } from 'render/render.models';
import { DatesService } from 'shared/services/dates/dates.service';
import { SegmentationService, SegmentationParams } from 'shared/services/segmentation.service';

import { RenderConfigurationService } from './render-configuration.service';


@Injectable()
export class RenderPageService {

  constructor(
    private readonly http: HttpClient,
    private readonly renderConfigurationService: RenderConfigurationService,
    private readonly segmentationService: SegmentationService,
    private readonly datesService: DatesService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  public pageBySlug(
    slug: string,
    country: string,
    versionId?: string,
    lang?: string,
    device?: string,
    timeTravelTo?: string,
    userFlags?: string
  ): Observable<PageForRender | undefined> {
    if (this.renderConfigurationService.config.staticMaterializedConfig === undefined) {
      const url = this.getPageBySlugUrl(
        this.renderConfigurationService.config,
        slug,
        country,
        versionId,
        lang,
        device,
        timeTravelTo,
        userFlags
      );
      const headers = new HttpHeaders({
        'Content-Type': 'application/json; charset=utf-8'
      });
      return this.http.get<PageForRender>(url, { headers }).pipe(
        catchError((error: HttpErrorResponse) => {
          console.error({ message: `Error retrieving pageBySlug: ${url}`, error });
          this.dispatchErrorEvent(slug, error);
          return Promise.reject(error.message || error);
        })
      );
    }
    return this.getStaticPageContent(country, slug, lang, userFlags, timeTravelTo);
  }

  private getStaticPageName(
    config: AppConfig,
    pageSlug: string,
    country: string,
    lang: string,
    userFlags?: string
  ): string {
    let contentsDestPath;

    if (country.toLowerCase() === 'cn') {
      contentsDestPath = config.staticMaterializedConfig!.staticMaterializedUrlChina;
    } else {
      contentsDestPath = config.staticMaterializedConfig!.staticMaterializedUrl;
    }
    const filename = `${country}-${lang}.json`;
    const vipPathOrNothing = userFlags?.includes('vip') ? 'vip/' : '';
    return `${contentsDestPath}/${pageSlug}/webApp/${vipPathOrNothing}${filename}`;
  }

  private getStaticPageContent(
    country: string,
    slug: string,
    lang?: string,
    userFlags?: string,
    timeTravelTo?: string
  ): Observable<PageForRender | undefined> {
    const ilang: string = lang || 'en';

    const userFlagsObj = this.segmentationService.parseUserFlags(userFlags);

    let currentdt: DateTime | undefined;

    if (timeTravelTo !== undefined) {
      this.datesService.setTimeTravelTo(timeTravelTo);
    }

    return this.datesService.getCurrentDate(country)
      .pipe(
        tap((date: DateTime) => {
          currentdt = date;
        }),
        switchMap(() => this.http.get<{ segments: PageForRender[] }>(
          this.getStaticPageName(this.renderConfigurationService.config, slug, country, ilang, userFlags)
        )),
        map((res) => {
          const segments = res.segments;
          const params = {
            userFlags: userFlagsObj,
            currentdt: currentdt!
          };

          // TODO: CMS-6814
          const matchingSegment = this.findMatchingSegment(segments, params as unknown as SegmentationParams);
          delete matchingSegment?.criteria;
          return matchingSegment;
        })
      );
  }

  private dispatchErrorEvent(slug: string, error: HttpErrorResponse): void {
    this.document.dispatchEvent(new CustomEvent('slug-requested-error', {
      bubbles: true,
      cancelable: false,
      detail: {
        slug,
        ...error
      }
    }));
  }

  private findMatchingSegment(segments: PageForRender[], params: SegmentationParams): PageForRender | undefined {
    const matchParams: SegmentationParams = {
      userFlags: params.userFlags,
      currentdt: params.currentdt
    };

    return segments.find((o) => this.segmentationService.segmentMatches(o, matchParams));
  }

  private getPageBySlugUrl(
    config: RenderConfiguration,
    slug: string,
    country: string,
    versionId?: string,
    lang?: string,
    device?: string,
    timeTravelTo?: string,
    userFlags?: string
  ): string {
    const url = `${config.apiMaterializedUrl}/page/${slug}?country=${country}${lang ? `&lang=${lang}` : ''}`
      + `${versionId ? `&versionId=${versionId}` : ''}`
      + `${device ? `&device=${device}` : ''}${timeTravelTo ? `&timeTravelTo=${timeTravelTo}` : ''}`
      + `${userFlags ? `&userFlags=${userFlags}` : ''}`;
    return url;
  }

}
